import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { JourneyProvider } from "./contexts/JourneyContext";
import { initializeReminderChecker, loadReminderSettings } from "./utils/notifications";
import { attachGlobalErrorReporter } from "./utils/clientErrorReporter";
import AppErrorBoundary from "./components/AppErrorBoundary";

// EAGER — critical first-paint surface (landing → register → dashboard).
// Everything else is lazy-loaded via React.lazy below.
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import OnboardingQuiz from "./pages/OnboardingQuiz";
import Subscription from "./pages/Subscription";

// LAZY — non-critical routes (drops ~50 page modules out of the initial bundle).
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Affirmations = lazy(() => import("./pages/Affirmations"));
const AngelNumbers = lazy(() => import("./pages/AngelNumbers"));
const SpiritAnimals = lazy(() => import("./pages/SpiritAnimals"));
const Journal = lazy(() => import("./pages/Journal"));
const Music = lazy(() => import("./pages/Music"));
const CustomAffirmations = lazy(() => import("./pages/CustomAffirmations"));
const GuidedMeditations = lazy(() => import("./pages/GuidedMeditations"));
const SpiritualAwakening = lazy(() => import("./pages/SpiritualAwakening"));
const SpiritualArticle = lazy(() => import("./pages/SpiritualArticle"));
const ReminderSettings = lazy(() => import("./pages/ReminderSettings"));
const Profile = lazy(() => import("./pages/Profile"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const ManageSubscription = lazy(() => import("./pages/ManageSubscription"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const NutritionExercise = lazy(() => import("./pages/NutritionExercise"));
const WorkoutChallenges = lazy(() => import("./pages/WorkoutChallenges"));
const PomodoroTimer = lazy(() => import("./pages/PomodoroTimer"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const BedtimeRoutine = lazy(() => import("./pages/BedtimeRoutine"));
const MorningRoutine = lazy(() => import("./pages/MorningRoutine"));
const Favorites = lazy(() => import("./pages/Favorites"));
const MindfulHobbies = lazy(() => import("./pages/MindfulHobbies"));
const ShareQR = lazy(() => import("./pages/ShareQR"));
const MarketingMaterials = lazy(() => import("./pages/MarketingMaterials"));
const BabyGoToTherapy = lazy(() => import("./pages/BabyGoToTherapy"));
const RootedResources = lazy(() => import("./pages/RootedResources"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminHealth = lazy(() => import("./pages/AdminHealth"));
const WellnessJourney = lazy(() => import("./pages/WellnessJourney"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const TestMatrixUnlock = lazy(() => import("./pages/TestMatrixUnlock"));
const ManifestEvents = lazy(() => import("./pages/ManifestEvents"));
const Readables = lazy(() => import("./pages/Readables"));
const CourageousConversations = lazy(() => import("./pages/CourageousConversations"));
const GamePlay = lazy(() => import("./pages/GamePlay"));
const TeamProgress = lazy(() => import("./pages/TeamProgress"));
const CourageDeckLeaderboard = lazy(() => import("./pages/CourageDeckLeaderboard"));
const SoulCircle = lazy(() => import("./pages/SoulCircle"));
const SoulProfile = lazy(() => import("./pages/SoulProfile"));
const DreamJournal = lazy(() => import("./pages/DreamJournal"));
const VibeCheck = lazy(() => import("./pages/VibeCheck"));
const SymptomJournal = lazy(() => import("./pages/SymptomJournal"));
const GroundingHour = lazy(() => import("./pages/GroundingHour"));

import InstallPrompt from "./components/InstallPrompt";
import InstallHelpButton from "./components/InstallHelpButton";
import OfflineIndicator from "./components/OfflineIndicator";
import WhatsNew from "./components/WhatsNew";
import SoulCardInviteBanner from "./components/SoulCardInviteBanner";
import { initOfflineQueue } from "./utils/offlineQueue";
import "./App.css";

// Sacred loading fallback for lazy routes — calm gradient + breathing dot.
const RouteLoader = () => (
  <div
    className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-amber-50 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900"
    data-testid="route-loader"
  >
    <div className="w-3 h-3 rounded-full bg-gradient-to-br from-emerald-500 to-amber-500 animate-pulse" />
  </div>
);

// Register the PWA service worker as early as possible so offline caching
// kicks in on first load — the push-notification init path also registers it
// but only after permission grant, which gates offline support unnecessarily.
if (typeof window !== "undefined" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((reg) => {
        // Detect when a new SW version is ready and auto-reload so users
        // don't stay on a stale cached bundle after we ship UI changes.
        // Without this, tab-cached app shells can run old code for days.
        if (reg.waiting) {
          reg.waiting.postMessage({ type: "SKIP_WAITING" });
        }
        reg.addEventListener("updatefound", () => {
          const next = reg.installing;
          if (!next) return;
          next.addEventListener("statechange", () => {
            if (next.state === "installed" && navigator.serviceWorker.controller) {
              // A new version is waiting. Activate it immediately and
              // reload the page one time so the user sees fresh code.
              next.postMessage({ type: "SKIP_WAITING" });
            }
          });
        });
        // Reload once when the SW controller swaps — guard against infinite
        // loops with a sessionStorage flag so a single session refreshes at
        // most once.
        let reloading = false;
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          if (reloading) return;
          reloading = true;
          try {
            if (!sessionStorage.getItem("ll_sw_reloaded")) {
              sessionStorage.setItem("ll_sw_reloaded", "1");
              window.location.reload();
            }
          } catch {
            window.location.reload();
          }
        });
      })
      .catch((err) => console.warn("[SW] registration failed:", err));
  });
}

// Boot the IndexedDB-backed offline writes queue (auto-flushes on reconnect).
initOfflineQueue();

// Initialize reminder checker on app load
const initReminders = () => {
  const settings = loadReminderSettings();
  if (settings?.enabled) {
    // Request permission if not already granted
    if (Notification.permission === "granted") {
      initializeReminderChecker();
      console.log("🔔 Reminder checker started - permission granted");
    } else if (Notification.permission === "default") {
      // Don't auto-request, but log that permission is needed
      console.log("🔔 Reminders ready - waiting for notification permission");
    }
  }
};

// Call initialization
initReminders();

// Scroll to top on route change
const ScrollToTop = () => {
  const location = useLocation();
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname, location.search]);
  
  return null;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary text-xl font-serif">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary text-xl font-serif">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  // Wire global JS error + unhandledrejection → /api/client-errors once.
  useEffect(() => {
    attachGlobalErrorReporter();
  }, []);

  return (
    <AppErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <JourneyProvider>
            <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<RouteLoader />}>
              <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
              <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
              <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/affirmations" element={<ProtectedRoute><Affirmations /></ProtectedRoute>} />
              <Route path="/angel-numbers" element={<ProtectedRoute><AngelNumbers /></ProtectedRoute>} />
            <Route path="/spirit-animals" element={<ProtectedRoute><SpiritAnimals /></ProtectedRoute>} />
            <Route path="/journal" element={<ProtectedRoute><Journal /></ProtectedRoute>} />
            <Route path="/music" element={<ProtectedRoute><Music /></ProtectedRoute>} />
            <Route path="/custom-affirmations" element={<ProtectedRoute><CustomAffirmations /></ProtectedRoute>} />
            <Route path="/guided-meditations" element={<ProtectedRoute><GuidedMeditations /></ProtectedRoute>} />
            <Route path="/spiritual-awakening" element={<ProtectedRoute><SpiritualAwakening /></ProtectedRoute>} />
            <Route path="/spiritual-awakening/:articleId" element={<ProtectedRoute><SpiritualArticle /></ProtectedRoute>} />
            <Route path="/reminder-settings" element={<ProtectedRoute><ReminderSettings /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/subscription" element={<Subscription />} />
            <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
            <Route path="/subscription/manage" element={<ProtectedRoute><ManageSubscription /></ProtectedRoute>} />
            <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
            <Route path="/nutrition" element={<ProtectedRoute><NutritionExercise /></ProtectedRoute>} />
            <Route path="/workout-challenges" element={<ProtectedRoute><WorkoutChallenges /></ProtectedRoute>} />
            <Route path="/pomodoro" element={<ProtectedRoute><PomodoroTimer /></ProtectedRoute>} />
            <Route path="/bedtime" element={<ProtectedRoute><BedtimeRoutine /></ProtectedRoute>} />
            <Route path="/morning" element={<ProtectedRoute><MorningRoutine /></ProtectedRoute>} />
            <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
            <Route path="/share" element={<ProtectedRoute><ShareQR /></ProtectedRoute>} />
            <Route path="/marketing" element={<ProtectedRoute><MarketingMaterials /></ProtectedRoute>} />
            <Route path="/mindful-hobbies" element={<ProtectedRoute><MindfulHobbies /></ProtectedRoute>} />
            <Route path="/therapy" element={<ProtectedRoute><BabyGoToTherapy /></ProtectedRoute>} />
            <Route path="/rooted-resources" element={<ProtectedRoute><RootedResources /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/health" element={<ProtectedRoute><AdminHealth /></ProtectedRoute>} />
            <Route path="/wellness-journey" element={<ProtectedRoute><WellnessJourney /></ProtectedRoute>} />
            {/* Legal Pages - Public */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            
            {/* Events Page */}
            <Route path="/events" element={<ProtectedRoute><ManifestEvents /></ProtectedRoute>} />
            <Route path="/manifest-events" element={<ProtectedRoute><ManifestEvents /></ProtectedRoute>} />
            
            {/* Readables - Digital Reading Library */}
            <Route path="/readables" element={<ProtectedRoute><Readables /></ProtectedRoute>} />
            
            {/* Onboarding Quiz */}
            <Route path="/quiz" element={<ProtectedRoute><OnboardingQuiz /></ProtectedRoute>} />
            
            {/* Courageous Conversations Game */}
            <Route path="/courage-deck" element={<ProtectedRoute><CourageousConversations /></ProtectedRoute>} />
            <Route path="/courage-deck/play/:roomId" element={<ProtectedRoute><GamePlay /></ProtectedRoute>} />
            <Route path="/courage-deck/team/:teamId" element={<ProtectedRoute><TeamProgress /></ProtectedRoute>} />
            <Route path="/courage-deck/leaderboard" element={<ProtectedRoute><CourageDeckLeaderboard /></ProtectedRoute>} />
            
            {/* Soul Circle - Friends & Chat */}
            <Route path="/soul-circle" element={<ProtectedRoute><SoulCircle /></ProtectedRoute>} />
            <Route path="/soul-circle/profile/:userUuid" element={<ProtectedRoute><SoulProfile /></ProtectedRoute>} />
            
            {/* Dream Journal */}
            <Route path="/dreams" element={<ProtectedRoute><DreamJournal /></ProtectedRoute>} />
            
            {/* Vibe Check & Symptom Journal */}
            <Route path="/vibe-check" element={<ProtectedRoute><VibeCheck /></ProtectedRoute>} />
            <Route path="/symptom-journal" element={<ProtectedRoute><SymptomJournal /></ProtectedRoute>} />
            
            {/* Grounding Hour - Digital Wellness */}
            <Route path="/grounding" element={<ProtectedRoute><GroundingHour /></ProtectedRoute>} />
            
            {/* Test Page - Remove before production */}
            <Route path="/test-matrix" element={<TestMatrixUnlock />} />
          </Routes>
          </Suspense>
          <Toaster position="top-right" />
          <InstallPrompt />
          <InstallHelpButton />
          <OfflineIndicator />
          <WhatsNew />
          <SoulCardInviteBanner />
        </BrowserRouter>
        </JourneyProvider>
      </AuthProvider>
    </ThemeProvider>
    </AppErrorBoundary>
  );
}

export default App;
